// Primitivo Input. Sempre acompanha label (a11y §13). Encurta a a11y obrigando
// `label` + `id` via props — sem InputField/FormControl wrapper extra.
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import styles from './Input.module.css';
import { cn } from '@/lib/utils/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** Mensagem de ajuda neutra abaixo do input. */
  hint?: string;
  /** Erro — quando preenchido, aplica visual de erro + aria-invalid. */
  error?: string;
  leadingIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leadingIcon, id, className, ...rest },
  ref,
) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const describedById = error ? `${inputId}-err` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className={cn(styles.field, className)}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <div className={cn(styles.wrapper, error && styles.error)}>
        {leadingIcon && <span className={styles.leading}>{leadingIcon}</span>}
        <input
          id={inputId}
          ref={ref}
          className={styles.input}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedById}
          {...rest}
        />
      </div>
      {error && (
        <p id={`${inputId}-err`} className={styles.errorText} role="alert">
          {error}
        </p>
      )}
      {!error && hint && (
        <p id={`${inputId}-hint`} className={styles.hint}>
          {hint}
        </p>
      )}
    </div>
  );
});
